import { getDatabase, ref, set, get, child, update, push } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { createValueWithMetadata } from '../types/implementations';
import { ValueStatus } from '../types/protocols';
import { formatDateForStorage } from '../Helpers/DateUtils';
import { BaseType, BuildComponentTypeDefinition, CostUnit, UtilityCategory } from '../types/Enums';
import { componentDeletionUpdates } from '../Helpers/componentDeletion';
import { requireAdmin } from './adminGuard';
import { recordActivity } from './activityApi';
import { ComponentFieldPatch } from '../Helpers/componentPatch';
import { REQUIRED_DEFAULT_TYPES, defaultTypeDefinition, missingDefaultTypes } from '../Helpers/requiredDefaultTypes';

/** Lifetime and replacement cost a new type is created with. */
export interface NewTypeLifetime {
    expectedLifetimeYears: number;
    replacementCost: number;
    costUnit: CostUnit;
}

/** Fields that can be edited via this API */
const EDITABLE_FIELDS = [
    'specificType',
    'label',
    'cardinalDirection',
    'roofShape',
    'material',
    'brand',
    'comment'
] as const;

type EditableField = typeof EDITABLE_FIELDS[number];

/**
 * Describes the fields that were persisted, so callers can apply the exact same
 * change to local state without re-reading from Firebase.
 */
export type { ComponentFieldPatch };

/**
 * API for updating component fields with proper ValueWithMetadata tracking.
 * Follows the pattern from componentWarrantyApi.ts.
 */
class ComponentFieldsApi {
    private db = getDatabase();

    /**
     * Update a component field using ValueWithMetadata pattern.
     * @param buildingKey The building key
     * @param componentKey The component key
     * @param fieldPath The field path (must be one of the allowed editable fields)
     * @param value The new value, or null to remove the field
     */
    async updateField<T>(
        buildingKey: string,
        componentKey: string,
        fieldPath: EditableField,
        value: T | null
    ): Promise<ComponentFieldPatch> {
        const user = getAuth().currentUser;
        if (!user) throw new Error('Du måste vara inloggad');

        // Validate field path
        if (!EDITABLE_FIELDS.includes(fieldPath)) {
            throw new Error('Otillåtet fält');
        }

        const fieldRef = ref(
            this.db,
            `buildings/${buildingKey}/structure/components/${componentKey}/${fieldPath}`
        );

        if (value === null) {
            // Write an unset ValueWithMetadata instead of deleting the node so
            // the change-log trigger can attribute the removal. iOS does the
            // same for ValueWithMetadata clears, but still node-deletes
            // specificType/elevatorType — attributing those needs an iOS change.
            const data = createValueWithMetadata(null, user.uid, ValueStatus.notSet);
            await set(fieldRef, data);
            recordActivity('component.updated', { buildingKey, targetKey: componentKey });
            return { [fieldPath]: data };
        } else {
            const data = createValueWithMetadata(value, user.uid);
            await set(fieldRef, data);
            recordActivity('component.updated', { buildingKey, targetKey: componentKey });
            return { [fieldPath]: data };
        }
    }

    /**
     * Update a component's condition assessment.
     * Condition uses a nested ValueWithMetadata shape with an extra `assessedAt`
     * field, so it is handled separately from the generic editable fields.
     *
     * @param buildingKey The building key
     * @param componentKey The component key
     * @param value The condition value (0–10)
     */
    async updateCondition(
        buildingKey: string,
        componentKey: string,
        value: number
    ): Promise<void> {
        const user = getAuth().currentUser;
        if (!user) throw new Error('Du måste vara inloggad');

        const timestamp = formatDateForStorage(new Date());
        const conditionRef = ref(
            this.db,
            `buildings/${buildingKey}/structure/components/${componentKey}/condition`
        );

        await set(conditionRef, {
            value: { value },
            updatedWhen: timestamp,
            updatedBy: user.uid,
            status: ValueStatus.set,
            assessedAt: timestamp
        });
        recordActivity('component.updated', { buildingKey, targetKey: componentKey });
    }

    /**
     * Atomically set specificType together with the category for Utility/Outdoor
     * components, so the two can never disagree in the database.
     * Category is saved as plain enum value, not ValueWithMetadata (per iOS).
     *
     * @param buildingKey The building key
     * @param componentKey The component key
     * @param typeKey The new specific type key, or null to save the category alone —
     *                an existing type that doesn't belong to the category is removed
     *                (iOS behavior)
     * @param category The category the type belongs to
     * @param categoryField 'utilityCategory' or 'outdoorCategory'
     * @param buildComponentTypes The component types config to check category membership
     */
    async setTypeAndCategory(
        buildingKey: string,
        componentKey: string,
        typeKey: string | null,
        category: string,
        categoryField: 'utilityCategory' | 'outdoorCategory',
        buildComponentTypes: Record<string, BuildComponentTypeDefinition>
    ): Promise<ComponentFieldPatch> {
        const user = getAuth().currentUser;
        if (!user) throw new Error('Du måste vara inloggad');

        const componentRef = ref(
            this.db,
            `buildings/${buildingKey}/structure/components/${componentKey}`
        );

        const updates: Record<string, unknown> = {};

        if (typeKey) {
            updates.specificType = createValueWithMetadata(typeKey, user.uid);
        } else {
            // Category-only save: clear an existing type that doesn't belong to
            // the new category
            const specificTypeSnapshot = await get(child(componentRef, 'specificType'));
            const currentType = specificTypeSnapshot.val()?.value;
            if (currentType && buildComponentTypes[currentType]?.category !== category) {
                updates.specificType = createValueWithMetadata(null, user.uid, ValueStatus.notSet);
            }
        }

        // Clear elevatorType when the utility category is not Elevator (match iOS behavior)
        if (categoryField === 'utilityCategory' && category !== UtilityCategory.Elevator) {
            const elevatorTypeSnapshot = await get(child(componentRef, 'elevatorType'));
            // Only clear a SET value — an existing unset tombstone must not be
            // re-stamped with the current editor's uid on every category save
            if (elevatorTypeSnapshot.val()?.value != null) {
                updates.elevatorType = createValueWithMetadata(null, user.uid, ValueStatus.notSet);
            }
        }

        updates[categoryField] = category;

        // Single multi-path update: type and category change together or not at all
        await update(componentRef, updates);
        recordActivity('component.updated', { buildingKey, targetKey: componentKey });

        return updates;
    }

    /**
     * Delete a component from its building (admin only), by the same rule as
     * iOS (`componentDeletionUpdates`): its locks go with it, the components on
     * it are detached, the pins in its photos are removed and its files are
     * tombstoned, in one root update. The change-log trigger archives the deletion.
     */
    async deleteComponent(buildingKey: string, componentKey: string): Promise<void> {
        const uid = await requireAdmin(this.db, 'ta bort komponenter');

        const componentsSnapshot = await get(
            ref(this.db, `buildings/${buildingKey}/structure/components`)
        );
        const components = componentsSnapshot.val() || {};
        if (!components[componentKey]) return;

        await update(
            ref(this.db),
            componentDeletionUpdates(components, buildingKey, componentKey, uid, formatDateForStorage(new Date()))
        );
        const label = components[componentKey]?.label;
        recordActivity('component.deleted', { buildingKey, targetKey: componentKey, label: typeof label === 'string' ? label : undefined });
    }

    /**
     * Create a new custom component type, with its lifetime and replacement
     * cost in the same write so a type is never left without them.
     * @param baseType The base type for the new custom type
     * @param name The display name (Swedish)
     * @param lifetime Expected lifetime (> 0 years) and replacement cost (0 = no cost)
     * @param category Optional category for Utility/Outdoor types
     * @returns The generated type key
     */
    async createCustomType(
        baseType: BaseType,
        name: string,
        lifetime: NewTypeLifetime,
        category?: string
    ): Promise<string> {
        const user = getAuth().currentUser;
        if (!user) throw new Error('Du måste vara inloggad');
        if (!(lifetime.expectedLifetimeYears > 0)) throw new Error('Livslängden måste vara större än 0');
        if (!Number.isFinite(lifetime.replacementCost) || lifetime.replacementCost < 0) {
            throw new Error('Utbyteskostnaden kan inte vara negativ');
        }

        // Same key format as iOS: {baseType}_custom_{push key}
        const pushKey = push(ref(this.db, 'config/buildComponentTypes')).key;
        const newTypeKey = `${baseType}_custom_${pushKey}`;

        const newTypeDefinition: BuildComponentTypeDefinition = {
            baseType,
            translations: {
                sv: name.trim()
            },
            ...(category ? { category } : {}),
            maintenance: {
                lifetime: {
                    expectedLifetimeYears: lifetime.expectedLifetimeYears,
                    replacementCost: lifetime.replacementCost,
                    costUnit: lifetime.costUnit
                }
            },
            createdBy: user.uid,
            createdAt: formatDateForStorage(new Date())
        };

        const typeRef = ref(this.db, `config/buildComponentTypes/${newTypeKey}`);
        await set(typeRef, newTypeDefinition);
        recordActivity('settings.updated', { targetKey: newTypeKey, label: name.trim() });

        return newTypeKey;
    }

    /**
     * Creates the REQUIRED_DEFAULT_TYPES missing from `config/buildComponentTypes`
     * (admin, from the setup banner or Konfigurationsstatus). Existing types are
     * never touched. Returns the keys it created.
     */
    async ensureDefaultTypes(): Promise<string[]> {
        const uid = await requireAdmin(this.db, 'skapa standardtyper');
        const snapshots = await Promise.all(
            REQUIRED_DEFAULT_TYPES.map(entry => get(ref(this.db, `config/buildComponentTypes/${entry.key}`)))
        );
        const existing = Object.fromEntries(
            REQUIRED_DEFAULT_TYPES.map((entry, i) => [entry.key, snapshots[i].exists()])
        );
        const missing = missingDefaultTypes(existing);
        if (missing.length === 0) return [];

        const now = formatDateForStorage(new Date());
        const updates: Record<string, BuildComponentTypeDefinition> = {};
        for (const entry of missing) {
            updates[`config/buildComponentTypes/${entry.key}`] = defaultTypeDefinition(entry, uid, now);
        }
        await update(ref(this.db), updates);
        recordActivity('settings.updated');
        return missing.map(entry => entry.key);
    }
}

export const componentFieldsApi = new ComponentFieldsApi();
